import { useEffect, useId, useState } from "react";
import type { Instrument } from "@alpha/shared";
import { api, errorText, qs } from "../api";

interface Props {
  onSelect: (i: Instrument) => void;
  placeholder?: string;
  label?: string;
}

/** Ticker or name search against /v1/instruments. Results are plain text buttons; nothing is rendered as HTML. */
export function InstrumentSearch({ onSelect, placeholder = "Search ticker or name", label = "Search instruments" }: Props) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<Instrument[]>([]);
  const [state, setState] = useState<"idle" | "loading" | "error">("idle");
  const [message, setMessage] = useState("");
  const listId = useId();

  useEffect(() => {
    const term = q.trim();
    if (term.length === 0) {
      setResults([]);
      setState("idle");
      return;
    }
    const ctrl = new AbortController();
    setState("loading");
    const timer = setTimeout(() => {
      api
        .get<{ items: Instrument[] }>(`/v1/instruments${qs({ q: term, take: 8, active: "true" })}`, ctrl.signal)
        .then((r) => {
          setResults(r.items);
          setState("idle");
        })
        .catch((e: unknown) => {
          if (ctrl.signal.aborted) return;
          setMessage(errorText(e));
          setState("error");
        });
    }, 200);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [q]);

  return (
    <div className="search">
      <input
        type="search"
        aria-label={label}
        aria-controls={listId}
        placeholder={placeholder}
        value={q}
        maxLength={40}
        onChange={(e) => setQ(e.target.value)}
        autoComplete="off"
      />
      {q.trim().length > 0 && (
        <ul id={listId} className="search-results" role="listbox" aria-label="Matches">
          {state === "loading" && <li className="muted pad">Searching...</li>}
          {state === "error" && <li className="error pad">{message}</li>}
          {state === "idle" && results.length === 0 && <li className="muted pad">No matches</li>}
          {results.map((r) => (
            <li key={r.id} role="option" aria-selected={false}>
              <button
                type="button"
                onClick={() => {
                  onSelect(r);
                  setQ("");
                }}
              >
                <strong>{r.ticker}</strong>
                <span className="muted"> {r.name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
